<template>
    <div class="my-timeline">
        <EmptyMessage
            v-if="items.length === 0 && !isLoading"
            text="Здесь пока ничего нет."
            class="my-timeline__empty-message"
        />
        <div class="my-timeline__border" ref="timelineBorder">
            <div
                v-if="isCurrentDayVisible"
                class="my-timeline__current-day-wrapper"
                :style="{ left: currentDayPosition }"
            >
                <CurrentDayForTimeline />
            </div>
            <div
                v-if="flagLeftPosition !== null"
                class="my-timeline__flag-wrapper"
                :style="{ left: flagLeftPosition + 'px' }"
            >
                <FlagForTimeline>
                    {{ endDateOfWindow }}
                </FlagForTimeline>
            </div>
            <div class="my-timeline__scroll">
                <div class="my-timeline__grid">
                    <div
                        v-for="item in itemsWithPositions"
                        :key="item.id + item.name + windowLabel"
                        :ref="`item-${item.id}`"
                        :class="[
                            `my-timeline__item my-timeline__item--${item.type}`,
                            {
                                'my-timeline__item--short': item.isShort,
                                'my-timeline__item--animate': true,
                            },
                        ]"
                        :style="{
                            gridColumn: item.startSeg + '/' + item.endSeg,
                            gridRow: item.row,
                            '--row-index': item.row - 1,
                        }"
                        @click="clickOnItem(item)"
                        @mouseenter="showTooltip($event, item)"
                        @mouseleave="hideTooltip"
                    >
                        <div class="my-timeline__item-icon" :class="`my-timeline__item-icon--${item.type}`">
                            <component :is="TYPES[item.type].icon" />
                        </div>
                        <span class="my-timeline__item-name">{{ item.name }}</span>
                    </div>
                </div>
            </div>
            <!-- Глобальный тултип -->
            <div v-if="tooltipData" class="my-timeline__tooltip" :style="tooltipStyle">
                <div class="my-timeline__tooltip-content">
                    <h3 class="my-timeline__tooltip-title">{{ tooltipData.name }}</h3>
                    <div class="my-timeline__tooltip-dates">
                        <span>{{ formatDate(tooltipData.dateStart) }} – {{ formatDate(tooltipData.dateEnd) }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="my-timeline__month-labels" :class="{ 'my-timeline__month-labels--loading': isLoading }">
            <div
                v-for="(month, index) in visibleMonths"
                :key="month.format('MMMM')"
                class="my-timeline__month-label"
                :style="{ '--column-index': index }"
            >
                {{ month.format('MMMM') }}
            </div>
        </div>
        <footer class="my-timeline__footer" v-if="items.length !== 0">
            <div class="my-timeline__footer-item" v-for="(item, key) in TYPES" :key="key + item.name">
                <div class="my-timeline__footer-item-icon" :class="`my-timeline__footer-item-icon--${key}`">
                    <component :is="item.icon" />
                </div>
                {{ item.name }}
            </div>

            <div class="my-timeline__header">
                <button class="my-timeline__nav-btn" @click="prevWindow" :disabled="isPrevDisabled">
                    <svg width="16" height="16">
                        <use xlink:href="#pagination-chevron-left" />
                    </svg>
                </button>
                <div class="my-timeline__label">{{ windowLabel }}</div>
                <button class="my-timeline__nav-btn" @click="nextWindow" :disabled="isNextDisabled">
                    <svg width="16" height="16">
                        <use xlink:href="#pagination-chevron-right" />
                    </svg>
                </button>
            </div>
        </footer>
    </div>
</template>

<script>
import moment from 'moment';
import 'moment/locale/ru';
import IconProject from '@/views/track/icons/IconProject.vue';
import IconCourse from '@/views/track/icons/IconCourse.vue';
import IconInternship from '@/views/track/icons/IconInternship.vue';
import CurrentDayForTimeline from '@/views/track/components/CurrentDayForTimeline.vue';
import FlagForTimeline from '@/views/track/components/FlagForTimeline.vue';
import EmptyMessage from '@/views/track/components/tabs/components/parts/EmptyMessage.vue';

export default {
    name: 'MyTimeline',
    components: {
        IconProject,
        IconCourse,
        IconInternship,
        CurrentDayForTimeline,
        FlagForTimeline,
        EmptyMessage,
    },
    props: {
        items: {
            type: Array,
            required: true,
        },
        isLoading: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            TYPES: {
                project: {
                    name: 'Проекты',
                    icon: 'IconProject',
                },
                event: {
                    name: 'Обучение',
                    icon: 'IconCourse',
                },
                internship: {
                    name: 'Стажировки',
                    icon: 'IconInternship',
                },
            },
            windowStart: null,
            activeTooltipId: null,
            tooltipData: null,
            tooltipStyle: {
                top: '0px',
                left: '0px',
            },
            flagLeftPosition: null,
        };
    },
    created() {
        // Инициализируем окно на начало полугодия
        this.windowStart = this.getHalfYearStart(moment());
        // Устанавливаем русскую локализацию
        moment.locale('ru');
    },
    mounted() {
        // Инициализируем позицию флага после монтирования и отрисовки
        this.$nextTick(() => {
            this.updateFlagPosition();
        });
        // Добавляем обработчик события resize
        window.addEventListener('resize', this.updateFlagPosition);
    },
    beforeDestroy() {
        // Удаляем обработчик события resize перед уничтожением компонента
        window.removeEventListener('resize', this.updateFlagPosition);
    },
    computed: {
        myItems() {
            return this.items;
        },
        sortedItems() {
            return [...this.myItems].sort((a, b) => {
                return moment(b.dateEnd).valueOf() - moment(a.dateEnd).valueOf();
            });
        },
        // Фильтруем элементы, которые пересекают текущий период
        filteredItems() {
            const start = this.windowStart;
            const end = this.windowEnd;
            return this.sortedItems.filter(
                (item) => moment(item.dateEnd).isAfter(start) && moment(item.dateStart).isBefore(end)
            );
        },
        visibleMonths() {
            return Array.from({ length: 6 }, (_, i) => moment(this.windowStart).add(i, 'months'));
        },
        windowEnd() {
            return moment(this.windowStart).add(6, 'months');
        },
        windowLabel() {
            const start = this.windowStart.format('MMMM YYYY');
            const end = moment(this.windowStart).add(5, 'months').format('MMMM YYYY');
            return `${start} – ${end}`;
        },
        // Вычисляем дату окончания последнего элемента во всем списке items
        absoluteLatestEndDate() {
            if (!this.items.length) {
                return null;
            }

            const latestItem = [...this.items].sort(
                (a, b) => moment(b.dateEnd).valueOf() - moment(a.dateEnd).valueOf()
            )[0];

            return moment(latestItem.dateEnd);
        },
        endDateOfWindow() {
            if (!this.absoluteLatestEndDate) {
                return '';
            }
            return this.absoluteLatestEndDate.format('DD MMMM YYYY');
        },
        itemsWithPositions() {
            const rows = [];
            return this.filteredItems.map((item) => {
                const startOffset = moment(item.dateStart).isBefore(this.windowStart)
                    ? 0
                    : moment(item.dateStart).diff(this.windowStart, 'months', true) * 4;
                const endOffset = moment(item.dateEnd).isAfter(this.windowEnd)
                    ? 24
                    : moment(item.dateEnd).diff(this.windowStart, 'months', true) * 4;
                const startSeg = Math.floor(startOffset) + 1;
                const endSeg = Math.ceil(endOffset) + 1;
                let rowIndex = null;
                for (let i = 0; i < rows.length; i++) {
                    const row = rows[i];
                    const overlap = row.some((r) => !(endSeg <= r.startSeg || startSeg >= r.endSeg));
                    if (!overlap) {
                        row.push({ startSeg, endSeg });
                        rowIndex = i;
                        break;
                    }
                }
                if (rowIndex === null) {
                    rows.push([{ startSeg, endSeg }]);
                    rowIndex = rows.length - 1;
                }
                const width = endSeg - startSeg;
                return {
                    ...item,
                    startSeg,
                    endSeg,
                    row: rowIndex + 1,
                    isShort: width <= 2,
                };
            });
        },
        isPrevDisabled() {
            return false; // можно добавить логику блокировки
        },
        isNextDisabled() {
            return false; // можно добавить логику блокировки
        },
        currentDayPosition() {
            const now = moment();
            if (now.isBefore(this.windowStart) || now.isAfter(this.windowEnd)) {
                return null;
            }

            const diffMonths = now.diff(this.windowStart, 'months', true);
            const segment = Math.floor(diffMonths * 4);
            const segmentWidth = 100 / 24; // 24 сегмента в сетке
            return `${segment * segmentWidth}%`;
        },
        isCurrentDayVisible() {
            return this.currentDayPosition !== null;
        },
    },
    methods: {
        // Возвращает начало полугодия (январь или июль) для даты date
        getHalfYearStart(date) {
            const m = moment(date);
            const month = m.month();
            const year = m.year();
            return month < 6 ? moment({ year, month: 0, day: 1 }) : moment({ year, month: 6, day: 1 });
        },
        // Переходим к предыдущему полугодию, выравнивая по границе полугодия
        prevWindow() {
            const shifted = this.windowStart.clone().subtract(6, 'months');
            this.windowStart = this.getHalfYearStart(shifted);
        },
        // Переходим к следующему полугодию, выравнивая по границе полугодия
        nextWindow() {
            const shifted = this.windowStart.clone().add(6, 'months');
            this.windowStart = this.getHalfYearStart(shifted);
        },
        openTooltip(id) {
            event.stopPropagation();
            this.activeTooltipId = id;
        },
        closeTooltip() {
            this.activeTooltipId = null;
        },
        formatDate(date) {
            return moment(date).format('DD.MM.YYYY');
        },
        async clickOnItem(item) {
            switch (item.type) {
                case 'project':
                    window.open(`https://project.${window.rsvDomain}/${item.id}/`, '_blank');
                    break;
                case 'event':
                    window.open(item.eventSiteUrl, '_blank');
                    break;
                case 'internship':
                    window.open(`https://project.${window.rsvDomain}/${item.id}`, '_blank');
                    break;
            }
        },
        showTooltip(event, item) {
            const element = event.currentTarget;
            const rect = element.getBoundingClientRect();
            const scrollContainer = this.$el.querySelector('.my-timeline__scroll');
            const scrollRect = scrollContainer.getBoundingClientRect();

            this.tooltipData = item;

            this.tooltipStyle = {
                top: `${rect.bottom - scrollRect.top}px`,
                left: `${rect.left - scrollRect.left + rect.width / 2}px`,
                transform: 'translate(-50%, 8px)',
            };
        },
        hideTooltip() {
            this.tooltipData = null;
        },
        updateFlagPosition() {
            const timelineBorderElement = this.$refs.timelineBorder;
            const scrollContainer = this.$el.querySelector('.my-timeline__scroll');

            // Если нет активностей вообще, скрываем флаг
            if (!this.sortedItems || this.sortedItems.length === 0) {
                this.flagLeftPosition = null;
                return;
            }

            // Получаем самую последнюю активность и её дату окончания
            const lastActivity = this.sortedItems[0];
            const lastActivityEndDate = moment(lastActivity.dateEnd);

            // Если последняя активность находится в текущем окне,
            // показываем флаг рядом с этой активностью
            if (
                lastActivityEndDate.isSameOrBefore(this.windowEnd) &&
                lastActivityEndDate.isSameOrAfter(this.windowStart)
            ) {
                // Ищем этот элемент среди видимых
                const visibleLastItem = this.itemsWithPositions.find((item) => item.id === lastActivity.id);
                if (visibleLastItem) {
                    const itemRef = `item-${visibleLastItem.id}`;
                    let itemElement = this.$refs[itemRef];

                    // Если ref вернул массив, берем первый элемент
                    if (Array.isArray(itemElement)) {
                        itemElement = itemElement[0];
                    }

                    if (itemElement && timelineBorderElement) {
                        const timelineBorderRect = timelineBorderElement.getBoundingClientRect();
                        const itemRect = itemElement.getBoundingClientRect();
                        this.flagLeftPosition = itemRect.right - timelineBorderRect.left;
                        return;
                    }
                }
            }

            // Если последняя активность находится после текущего окна,
            // показываем флаг в конце текущего окна
            if (lastActivityEndDate.isAfter(this.windowEnd)) {
                if (scrollContainer) {
                    const gridElement = scrollContainer.querySelector('.my-timeline__grid');
                    if (gridElement && timelineBorderElement) {
                        const timelineBorderRect = timelineBorderElement.getBoundingClientRect();
                        const gridRect = gridElement.getBoundingClientRect();
                        this.flagLeftPosition = gridRect.right - timelineBorderRect.left;
                        return;
                    }
                }
            }

            // В остальных случаях (последняя активность до текущего окна) скрываем флаг
            this.flagLeftPosition = null;
        },
    },
    watch: {
        itemsWithPositions: {
            handler() {
                // Обновляем позицию флага после изменения списка видимых элементов
                this.$nextTick(() => {
                    this.updateFlagPosition();
                });
            },
            deep: true,
            immediate: true, // Выполнить сразу после монтирования
        },
        windowStart: {
            handler() {
                // Обновляем позицию флага при смене окна
                this.$nextTick(() => {
                    this.updateFlagPosition();
                });
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/rem';
@import '~@/styles/variables';

.my-timeline {
    flex-shrink: 0;

    &__header {
        display: flex;
        align-items: center;
        margin-left: auto;
    }

    &__nav-btn {
        background: none;
        border: none;
        font-size: urem(14);
        font-weight: 500;

        cursor: pointer;
        &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }

        svg {
            width: urem(14);
            height: urem(14);
        }
    }

    &__label {
        font-size: urem(14);
        font-weight: 500;
        line-height: urem(20);
        user-select: none;
    }

    &__month-labels {
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        text-align: center;
        margin-bottom: urem(8);
        column-gap: urem(1);
        padding-left: urem(2);
        padding-bottom: urem(2);
        padding-right: urem(16);
        pointer-events: none;

        &--loading {
            .my-timeline__month-label::after {
                animation: columnPulse 1s ease-in-out infinite;
                animation-delay: calc(var(--column-index) * 0.3s);
            }
        }

        & .my-timeline__month-label {
            position: relative;
            font-size: urem(16);
            line-height: urem(30);
            color: rgba(42, 38, 38, 1);
            text-transform: capitalize;
            padding-top: urem(8);

            &::after {
                display: block;
                content: '';
                position: absolute;
                right: 0;
                bottom: urem(38);
                height: urem(538);
                width: 100%;
                background: #f2f2f2;
                border-top-left-radius: urem(16);
                border-top-right-radius: urem(16);
                z-index: 0;
            }
        }
    }

    &__border {
        position: relative;

        &::before {
            content: '';
            position: absolute;
            bottom: urem(-3);
            left: 0;
            display: block;
            width: urem(1069);
            height: urem(1);
            background-color: rgba(217, 215, 231, 1);
            z-index: 2;
        }
        &::after {
            content: '';
            position: absolute;
            bottom: urem(-3);
            left: urem(-1);
            display: block;
            width: urem(1);
            height: 97%;
            background-color: rgba(217, 215, 231, 1);
            z-index: 2;
        }
    }

    &__scroll {
        overflow-y: auto;
        overflow-x: hidden;
        height: urem(538);
        padding-top: urem(100);
        padding-right: urem(10);
        position: relative;
        scrollbar-gutter: stable;
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        /* Стиль самого скроллбара */
        &::-webkit-scrollbar {
            width: rem(6); /* Ширина скроллбара */
        }
        /* Стиль трека (фона) скроллбара */
        &::-webkit-scrollbar-track {
            background: transparent; /* Цвет трека */
        }
        /* Стиль ползунка (то, что движется) */
        &::-webkit-scrollbar-thumb {
            background-color: #a2b1d5; /* Цвет ползунка */
            border-radius: rem(8); /* Скругление углов */
            border: none;
        }
        /* Стиль при наведении */
        &::-webkit-scrollbar-thumb:hover {
            background-color: #ccccd7; /* Цвет при наведении */
        }
    }

    &__grid {
        padding: urem(1);
        position: relative;
        display: grid;
        align-content: flex-end;
        align-items: center;
        grid-template-columns: repeat(24, 1fr);
        grid-auto-rows: urem(40);
        row-gap: urem(4);
        column-gap: urem(2);
        margin-top: auto;
        width: 100%;
    }

    /* Фоновые блоки по месяцам */
    &__month-bg {
        background-color: rgba(0, 0, 0, 0.03);
        /* чтобы фон был под элементами */
        position: relative;
        z-index: 0;
    }

    /* Элементы поверх фоновых блоков */
    &__item {
        position: relative;
        z-index: 1;
        border-radius: urem(10);
        padding: urem(6) urem(10);
        display: flex;
        align-items: center;
        gap: urem(6);
        font-size: urem(12);
        font-weight: 500;
        line-height: urem(16);
        opacity: 0;
        transform: translateY(-10px);
        cursor: pointer;
        flex-shrink: 0;

        &:hover {
            z-index: 10;
            .my-timeline__tooltip {
                opacity: 1;
                visibility: visible;
                transform: translate(-50%, 100%);
            }
        }

        &--animate {
            animation: itemAppear 0.3s ease forwards;
            animation-delay: calc(var(--row-index) * 0.05s);
        }

        &--short {
            justify-content: center;
            padding: urem(6) urem(4);

            .my-timeline__item-name {
                display: none;
            }
        }

        &--project {
            background-color: #dcf9ff;
            color: #77bac8;
        }
        &--event {
            background-color: #ebc8f1;
            color: #7e6b99;
        }
        &--internship {
            background-color: #f8dda7;
            color: #cea95f;
        }

        &--active {
            z-index: 7;
        }
    }

    &__item-icon {
        width: urem(28);
        height: urem(28);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &--project {
            background-color: #edfcff;
        }
        &--event {
            background-color: #f5e3f8;
        }
        &--internship {
            background-color: #fbeed3;
        }

        svg {
            width: urem(20);
            height: urem(20);
        }
    }

    &__item-name {
        display: block;
        font-size: urem(12);
        font-weight: 500;
        color: #251f45;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        text-overflow: ellipsis;
        overflow: hidden;
        word-break: break-word;
    }

    &__footer {
        display: flex;
        align-items: center;
        gap: urem(24);
        margin-top: urem(16);
        background: #f8f8f8;
        border-radius: urem(10);
        padding: urem(8) urem(16);
        width: calc(100% - #{urem(15)});
    }

    &__footer-item {
        display: flex;
        align-items: center;
        gap: urem(10);
        font-size: urem(14);
        font-weight: 500;
        line-height: urem(24);
    }

    &__footer-item-icon {
        width: urem(32);
        height: urem(32);
        border-radius: urem(10);
        display: flex;
        align-items: center;
        justify-content: center;

        &--project {
            background-color: #dcf9ff;
        }
        &--event {
            background-color: #ebc8f1;
        }
        &--internship {
            background-color: #f8dda7;
        }

        svg {
            width: urem(20);
            height: urem(20);
        }
    }

    &__tooltip {
        position: absolute;
        background: #ffffff;
        border-radius: urem(10);
        box-shadow: 0 urem(2) urem(16) rgba(70, 99, 171, 0.16);
        padding: urem(10);
        min-width: urem(200);
        z-index: 8;
        pointer-events: none;
        transition: opacity 0.2s ease;
    }

    &__tooltip-content {
        display: flex;
        flex-direction: column;
        gap: urem(6);
    }

    &__tooltip-title {
        font-size: urem(14);
        line-height: urem(20);
        font-weight: 500;
        color: #000000;
        margin: 0;
    }

    &__tooltip-dates {
        margin: 0;
        font-size: urem(14);
        color: #98a0a9;
    }

    &__current-day-wrapper {
        position: absolute;
        bottom: 0;
        width: urem(2);
        height: urem(420);
        z-index: 2;
        pointer-events: none;
    }

    &__flag-wrapper {
        position: absolute;
        bottom: 0;
        z-index: 3;
        width: urem(2);
        height: urem(415);
    }

    @keyframes itemAppear {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    &__empty-message {
        margin-top: 0;
        margin-bottom: urem(40);
    }

    &__global-tooltip {
        position: absolute;
        background: #ffffff;
        border-radius: urem(10);
        box-shadow: 0 urem(2) urem(16) rgba(70, 99, 171, 0.16);
        padding: urem(10);
        min-width: urem(200);
        z-index: 8;
        pointer-events: none;
    }

    @keyframes columnPulse {
        0% {
            background: #f2f2f2;
        }
        50% {
            background: #e0e0e0;
        }
        100% {
            background: #f2f2f2;
        }
    }
}
</style>
